
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MainLayout } from './components/layout/MainLayout';
import Login from './pages/Login';
import Verify from './pages/Verify';
import ResetPassword from './pages/ResetPassword';
import InviteRegistration from './pages/InviteRegistration';
import SuperAdminDashboard from './pages/dashboard/SuperAdminDashboard';
import SuperAdminDashboardV2 from './pages/dashboard/SuperAdminDashboardV2';
import BusinessListV2 from './pages/businesses/BusinessListV2';
import BusinessDetailV2 from './pages/businesses/BusinessDetailV2';
import GeneralSettingsV2 from './pages/settings/platform/GeneralSettingsV2';
import SecuritySettingsV2 from './pages/settings/platform/SecuritySettingsV2';
import BillingSettingsV2 from './pages/settings/platform/BillingSettingsV2';
import SubscriptionPlanEditorV2 from './pages/settings/platform/SubscriptionPlanEditorV2';
import RoleEditorV2 from './pages/settings/platform/RoleEditorV2';
import LLMProvidersSettingsV2 from './pages/settings/platform/LLMProvidersSettingsV2';
import NotFound from './pages/NotFound';
import BillingSettings from './pages/settings/platform/BillingSettings';
import GeneralSettings from './pages/settings/platform/GeneralSettings';
import BusinessList from './pages/businesses/BusinessList';
import BusinessDetail from './pages/businesses/BusinessDetail';
import { ProtectedRoute } from './utils/routeUtils';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NotificationProvider } from './context/NotificationContext';
import { FloatingToastProvider } from './context/FloatingToastContext';
import SecuritySettings from './pages/settings/platform/SecuritySettings';
import RoleEditor from './pages/settings/platform/RoleEditor';
import LLMProvidersSettings from './pages/settings/platform/LLMProvidersSettings';
import ComplianceSettings from './pages/settings/platform/ComplianceSettings';
import CustomizationSettings from './pages/settings/platform/CustomizationSettings';
import CustomizationSettingsV2 from './pages/settings/platform/CustomizationSettingsV2';
import SubscriptionPlanEditor from './pages/settings/platform/SubscriptionPlanEditor';
import LegalDocuments from './pages/settings/platform/LegalDocuments';
import Index from './pages/Index';
import SupportIssuesV2 from './pages/support/SupportIssuesV2';
import SupportIssueDetailV2 from './pages/support/SupportIssueDetailV2';
import ContactRequestsV2 from './pages/contact/ContactRequestsV2';
import ContactRequestDetailV2 from './pages/contact/ContactRequestDetailV2';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const ProtectedRoutes = () => {
  const { user, needsVerification, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  
  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }
  
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  if (needsVerification || user.isVerified === false) {
    if (location.pathname === '/verify') {
      return (
        <Routes>
          <Route path="/verify" element={<Verify />} />
        </Routes>
      );
    }
    return <Navigate to="/verify" replace />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        {/* The classic interface, on its original paths. */}
        <Route index element={<Index />} />
        <Route path="/dashboard" element={ <SuperAdminDashboard /> } />
        <Route path="/businesses" element={ <BusinessList /> } />
        <Route path="/businesses/:businessId" element={ <BusinessDetail /> } />
        <Route path="/settings/platform/billing" element={ <BillingSettings /> } />
        <Route path="/settings/platform/subscription-plan" element={ <SubscriptionPlanEditor /> } />
        <Route path="/settings/platform/subscription-plan/:planId" element={ <SubscriptionPlanEditor /> } />
        <Route path="/settings/platform/general" element={ <GeneralSettings /> } />
        <Route path="/settings/platform/security" element={ <SecuritySettings /> } />
        <Route path="/settings/platform/security/role" element={ <RoleEditor /> } />
        <Route path="/settings/platform/security/role/:roleId" element={ <RoleEditor /> } />
        <Route path="/settings/platform/llm-providers" element={ <LLMProvidersSettings /> } />
        <Route path="/settings/platform/customization" element={ <CustomizationSettings /> } />
        <Route path="/settings/platform/legal" element={<LegalDocuments />} />

        {/* The redesigned one, built alongside it. Same pages, same data. */}
        <Route path="/v2" element={<Navigate to="/v2/dashboard" replace />} />
        <Route path="/v2/dashboard" element={ <SuperAdminDashboardV2 /> } />
        <Route path="/v2/businesses" element={ <BusinessListV2 /> } />
        <Route path="/v2/businesses/:businessId" element={ <BusinessDetailV2 /> } />
        <Route path="/v2/support-issues" element={<SupportIssuesV2 />} />
        <Route path="/v2/support-issues/:issueId" element={<SupportIssueDetailV2 />} />
        <Route path="/v2/contact-requests" element={<ContactRequestsV2 />} />
        <Route path="/v2/contact-requests/:requestId" element={<ContactRequestDetailV2 />} />
        <Route path="/v2/settings/platform/billing" element={ <BillingSettingsV2 /> } />
        <Route path="/v2/settings/platform/subscription-plan" element={ <SubscriptionPlanEditorV2 /> } />
        <Route path="/v2/settings/platform/subscription-plan/:planId" element={ <SubscriptionPlanEditorV2 /> } />
        <Route path="/v2/settings/platform/general" element={ <GeneralSettingsV2 /> } />
        <Route path="/v2/settings/platform/security" element={ <SecuritySettingsV2 /> } />
        <Route path="/v2/settings/platform/security/role" element={ <RoleEditorV2 /> } />
        <Route path="/v2/settings/platform/security/role/:roleId" element={ <RoleEditorV2 /> } />
        <Route path="/v2/settings/platform/llm-providers" element={ <LLMProvidersSettingsV2 /> } />
        <Route path="/v2/settings/platform/customization" element={ <CustomizationSettingsV2 /> } />
        <Route path="/v2/settings/platform/legal" element={<LegalDocuments />} />

        <Route path="*" element={<NotFound />} />
      </Route>

     
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Router>
          <AuthProvider>
            <NotificationProvider>
              <FloatingToastProvider>
                  <Routes>
                    {/* Public routes that don't require authentication */}
                    <Route path="/login" element={<Login />} />
                    
                    {/* Protected routes that require authentication */}
                    <Route path="/*" element={<ProtectedRoutes />} />
                  </Routes>
              </FloatingToastProvider>
            </NotificationProvider>
          </AuthProvider>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
